import dayjs from 'dayjs';
import type { TagCount, Task, TaskPriority } from '../../api/tasks/interface';

export type { TagCount };

export type DueBucket = 'overdue' | 'today' | 'upcoming' | 'none';

export const DATE_FORMAT = 'YYYY-MM-DD';

const PRIORITY_ORDER: Record<TaskPriority, number> = {
  high: 0,
  med: 1,
  low: 2,
};

export const PRIORITY_CYCLE: TaskPriority[] = ['high', 'med', 'low'];

export const nextPriority = (p: TaskPriority): TaskPriority =>
  PRIORITY_CYCLE[(PRIORITY_CYCLE.indexOf(p) + 1) % PRIORITY_CYCLE.length];

export const dueBucket = (
  due: string | null,
  now: dayjs.Dayjs = dayjs()
): DueBucket => {
  if (!due) {
    return 'none';
  }
  // `due` is a date-only ISO string (YYYY-MM-DD); dayjs parses it as local
  // midnight, so bucketing matches the user's calendar day.
  const d = dayjs(due);
  if (!d.isValid()) {
    return 'none';
  }
  const today = now.startOf('day');
  const day = d.startOf('day');
  if (day.isBefore(today)) {
    return 'overdue';
  }
  if (day.isSame(today)) {
    return 'today';
  }
  return 'upcoming';
};

export const formatDue = (due: string | null): string => {
  if (!due) {
    return '';
  }
  const d = dayjs(due);
  return d.isValid() ? d.format('MMM D') : due;
};

const byPriorityThenDue = (a: Task, b: Task): number => {
  const pa = PRIORITY_ORDER[a.priority] ?? PRIORITY_ORDER.med;
  const pb = PRIORITY_ORDER[b.priority] ?? PRIORITY_ORDER.med;
  if (pa !== pb) {
    return pa - pb;
  }
  // Tasks with a due date sort ahead of those without; earlier due first.
  if (a.due && b.due) {
    return a.due < b.due ? -1 : a.due > b.due ? 1 : 0;
  }
  if (a.due) {
    return -1;
  }
  if (b.due) {
    return 1;
  }
  return 0;
};

export interface TaskGroup {
  key: DueBucket;
  label: string;
  items: Task[];
}

const GROUP_LABELS: { key: DueBucket; label: string }[] = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'today', label: 'Today' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'none', label: 'No date' },
];

export interface GroupedTasks {
  groups: TaskGroup[];
  done: Task[];
  openCount: number;
}

export const openBlockers = (tasks: Task[], task: Task): Task[] =>
  (task.blocked_by ?? [])
    .map((id) => tasks.find((t) => t.id === id))
    .filter((t): t is Task => Boolean(t) && t!.status !== 'done');

export const isBlocked = (tasks: Task[], task: Task): boolean =>
  openBlockers(tasks, task).length > 0;

// Distinct tags across the given tasks, with usage counts, most-used first
// (ties broken alphabetically). Powers the panel filter bar and the detail
// tag autocomplete. Tags are aggregated case-insensitively (canonical lower
// case) so casing variants collapse into one chip.
export const collectTags = (tasks: Task[]): TagCount[] => {
  const counts = new Map<string, number>();
  for (const task of tasks) {
    for (const tag of task.tags ?? []) {
      const key = tag.toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || (a.tag < b.tag ? -1 : 1));
};

// A task matches the active-tag filter when it carries ANY of the selected
// tags (union), compared case-insensitively. No active tags means everything
// matches.
export const matchesTags = (task: Task, activeTags: string[]): boolean => {
  if (activeTags.length === 0) {
    return true;
  }
  const taskTags = (task.tags ?? []).map((t) => t.toLowerCase());
  return activeTags.some((tag) => taskTags.includes(tag.toLowerCase()));
};

export const groupTasks = (
  tasks: Task[],
  now: dayjs.Dayjs = dayjs()
): GroupedTasks => {
  const open = tasks.filter((t) => t.status !== 'done');
  const done = tasks.filter((t) => t.status === 'done');

  const groups = GROUP_LABELS.map(({ key, label }) => ({
    key,
    label,
    items: open
      .filter((t) => dueBucket(t.due, now) === key)
      .sort(byPriorityThenDue),
  })).filter((g) => g.items.length > 0);

  return { groups, done, openCount: open.length };
};
